/**
 * TtsButton — Text-to-Speech button using Web Speech API.
 *
 * Renders a small speaker icon button. Tap to start reading,
 * tap again to stop. Uses the best available German voice.
 * Works on iOS Safari, Chrome, Firefox.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { Volume2, VolumeX } from "lucide-react";
import styles from "./ChatView.module.css";

interface TtsButtonProps {
  text: string;
}

// Check TTS availability at module level — prevents hook-rule violations
const hasTts = typeof window !== "undefined" && "speechSynthesis" in window;

// Strip markdown-like formatting for cleaner speech
function cleanTextForSpeech(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1")  // bold
    .replace(/\*(.*?)\*/g, "$1")      // italic
    .replace(/`([^`]+)`/g, "$1")      // inline code
    .replace(/```[\s\S]*?```/g, "")   // code blocks
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // links
    .replace(/https?:\/\/[^\s]+/g, "") // raw URLs
    .trim();
}

// Find the preferred German voice (from settings or best available)
function getGermanVoice(): SpeechSynthesisVoice | null {
  const voices = speechSynthesis.getVoices();

  // Check user preference from settings
  const storedName = localStorage.getItem("tts-voice");
  if (storedName) {
    const stored = voices.find((v) => v.name === storedName);
    if (stored) return stored;
  }

  // Fallback: prefer high-quality voices
  const preferred = ["Helena", "Anna", "Petra", "Google Deutsch", "Microsoft"];
  for (const name of preferred) {
    const match = voices.find(
      (v) => v.lang.startsWith("de") && v.name.includes(name)
    );
    if (match) return match;
  }
  // Fallback: any German voice
  return voices.find((v) => v.lang.startsWith("de")) ?? null;
}

export function TtsButton({ text }: TtsButtonProps) {
  const [speaking, setSpeaking] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (speechSynthesis.speaking) {
        speechSynthesis.cancel();
        setSpeaking(false);
        return;
      }

      const cleanText = cleanTextForSpeech(text);
      if (!cleanText) return;

      const utterance = new SpeechSynthesisUtterance(cleanText);
      utteranceRef.current = utterance;

      // Set German voice
      const voice = getGermanVoice();
      if (voice) {
        utterance.voice = voice;
        utterance.lang = voice.lang;
      } else {
        utterance.lang = "de-DE";
      }

      utterance.rate = 1.0;
      utterance.pitch = 1.0;

      utterance.onstart = () => setSpeaking(true);
      utterance.onend = () => setSpeaking(false);
      utterance.onerror = () => setSpeaking(false);

      speechSynthesis.speak(utterance);
    },
    [text]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hasTts && speechSynthesis.speaking) {
        speechSynthesis.cancel();
      }
    };
  }, []);

  // Ensure voices are loaded (some browsers load async)
  useEffect(() => {
    if (!hasTts) return;
    speechSynthesis.getVoices();
    const handleVoices = () => speechSynthesis.getVoices();
    speechSynthesis.addEventListener?.("voiceschanged", handleVoices);
    return () => {
      speechSynthesis.removeEventListener?.("voiceschanged", handleVoices);
    };
  }, []);

  // Don't render if TTS not available — check AFTER hooks to satisfy React rules
  if (!hasTts) return null;

  return (
    <button
      className={styles.ttsButton}
      onClick={handleClick}
      title={speaking ? "Vorlesen stoppen" : "Vorlesen"}
      type="button"
    >
      {speaking ? <VolumeX size={12} /> : <Volume2 size={12} />}
    </button>
  );
}
